import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import BASE_URL from '../../config';
import {
  Modal,
  Button,
  Spinner,
  Badge,
  Table,
  Input,
  Row,
  Col
} from 'reactstrap';
import Select from 'react-select';
import { 
  X, 
  MessageCircle, 
  User, 
  Search, 
  Calendar, 
  Package, 
  Receipt, 
  CreditCard, 
  FileText, 
  ShoppingBag,
  ChevronLeft,
  Phone,
  ArrowBigUpDash,
  TrendingUp,
  BarChart2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { searchCustomers, getCustomers } from '../Api/GmsCustomer';
import { getCustomerMemberships } from '../Api/GmsCustomerMembership';
import { getBookings, getCustomerUnbilledServices } from '../Api/GmsBooking';
import { getSalesInvoices, getCustomerOutstandingCredit, getInvoiceReportSummary, getInvoiceReportDetails } from '../Api/GmsBilling';
import { getReceipts } from '../Api/GmsReceipt';
import { getSpaServices } from '../Api/GmsSpaService';
import { getSpaPackages } from '../Api/GmsSpaPackage';
import { getSpaPackageSeries } from '../Api/GmsSpaPackageSeries';
import { getBusinesses } from '../Api/GmsBusiness';
import { getBranches } from '../Api/GmsBranch';
import { getCampaignReport } from '../Api/GmsCampaignReport';
import { getSourceBasedReportSummary } from '../Api/GmsSourceBasedReport';
import { getEmployees } from '../Api/GmsEmployee';
import { getDailyAttendance } from '../Api/GmsDailyAttendance';
import { getEmployeeLeaves } from '../Api/GmsEmployeeLeave';
import { getLeaveTypes } from '../Api/GmsLeaveType';
import { getProducts } from '../Api/GmsProduct';
import { getUserPermissions } from '../Api/GmsPermission';
import './spa-assistant.scss';

const SpaAssistant = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('categories'); // 'categories', 'subOptions', 'searching', 'result'
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [searchOptions, setSearchOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tabData, setTabData] = useState(null);
  const [tabLoading, setTabLoading] = useState(false);
  const [counts, setCounts] = useState({ memberships: 0, packages: 0 });
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [message, setMessage] = useState(null);
  const [adminBusinesses, setAdminBusinesses] = useState([]);
  const [adminBranches, setAdminBranches] = useState([]);
  const [selectedAdminBusiness, setSelectedAdminBusiness] = useState(null);
  const [selectedAdminBranch, setSelectedAdminBranch] = useState(null);
  const [initialHiDone, setInitialHiDone] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  const currencyCode = localStorage.getItem("currencyCode") || "AED";
  
  // Extract business and branch id for API filtering
  const authUserString = localStorage.getItem("authUser");
  const authUser = authUserString ? JSON.parse(authUserString) : null;
  const userBusinessId = authUser?.companyId || authUser?.Business_Id || authUser?.businessId || authUser?.business_Id || null;
  const userBranchId = authUser?.branchId || authUser?.Branch_Id || authUser?.branchId || authUser?.branch_Id || null;
  const isAdmin = authUser?.isAdmin === true || authUser?.IsAdmin === "True";

  // Effective IDs to use for filtering (either from session or from admin selection)
  const currentBusinessId = userBusinessId || selectedAdminBusiness?.value;
  const currentBranchId = userBranchId || selectedAdminBranch?.value;

  useEffect(() => {
    if (authUser?.userId) {
      loadPermissions();
    }
  }, [authUser?.userId]);

  const loadPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const perms = await getUserPermissions(authUser.userId);
      setUserPermissions(perms || []);
    } catch (err) {
      console.error("Error loading user permissions for assistant:", err);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const isPermitted = (pageId) => {
    if (isAdmin) return true;
    if (!pageId) return true; // Default to true if no pageId is assigned
    return userPermissions.some(p => p.permission_Page_Id === pageId && p.permission_View);
  };

  const categories = [
    { id: 'customer', name: 'Customer Management', icon: <User size={18} />, color: '#727cf5', pageId: 72 },
    { id: 'ops', name: 'Daily Operations', icon: <Calendar size={18} />, color: '#0acf97' },
    { id: 'fin', name: 'Financials & Billing', icon: <Receipt size={18} />, color: '#ffbc00' },
    { id: 'mkt', name: 'Marketing & Growth', icon: <ShoppingBag size={18} />, color: '#f9375e' },
    { id: 'hr', name: 'HR & Staff', icon: <User size={18} />, color: '#39afd1' },
  ];

  const subOptions = {
    customer: [
      { id: 'profile', name: 'Profile', icon: <User size={14} />, pageId: 72 },
      { id: 'notes', name: 'Notes', icon: <FileText size={14} />, pageId: 72 },
      { id: 'appointment', name: 'Appointments', icon: <Calendar size={14} />, pageId: 32 },
      { id: 'products', name: 'Products', icon: <ShoppingBag size={14} />, pageId: 39 },
      { id: 'memberships', name: 'Memberships', icon: <Package size={14} />, pageId: 42 },
      { id: 'packages', name: 'Packages', icon: <Package size={14} />, pageId: 37 },
      { id: 'unbilled', name: 'Unbilled', icon: <Receipt size={14} />, pageId: 38 },
      { id: 'paymentHistory', name: 'Payments', icon: <CreditCard size={14} />, pageId: 58 },
      { id: 'whatsapp', name: 'WhatsApp Messages', icon: <MessageCircle size={14} />, fullWidth: true, pageId: 92 },
    ],
    ops: [
      { id: 'summary', name: 'Daily Summary', icon: <FileText size={14} />, pageId: 54 },
      { id: 'today-bk', name: "Today's Bookings", icon: <Calendar size={14} />, pageId: 32 },
      { id: 'add-bk', name: 'Add Booking', icon: <ArrowBigUpDash size={14} />, pageId: 73 },
    ],
    fin: [
      { id: 'collection', name: 'Daily Collection', icon: <Receipt size={14} />, pageId: 54 },
      { id: 'outstanding', name: 'Outstanding List', icon: <CreditCard size={14} />, pageId: 39 },
    ],
    mkt: [
      { id: 'campaign-stats', name: 'Campaign Stats', icon: <TrendingUp size={14} />, pageId: 95 },
      { id: 'source-stats', name: 'Source Stats', icon: <BarChart2 size={14} />, pageId: 71 },
    ],
    hr: [
      { id: 'staff-duty', name: 'Staff on Duty', icon: <User size={14} />, pageId: 81 },
      { id: 'leave-req', name: 'Leave Requests', icon: <Calendar size={14} />, pageId: 10 },
    ]
  };

  // Filter accessible categories and sub-options
  const accessibleCategories = categories.filter(cat => {
    // Customer Management specific check
    if (cat.id === 'customer') return isPermitted(cat.pageId);
    
    // For other categories, check if any of their sub-options are permitted
    const accessibleSubOptions = subOptions[cat.id]?.filter(opt => isPermitted(opt.pageId)) || [];
    return accessibleSubOptions.length > 0;
  });

  const getVisibleSubOptions = (catId) => {
    return subOptions[catId]?.filter(opt => isPermitted(opt.pageId)) || [];
  };

  // Toggle chatbot visibility
  const toggleChatbot = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      resetFlow();
    }
  };

  const closeChatbot = () => {
    setIsOpen(false);
  };

  const resetFlow = () => {
    // If it's an admin without fixed IDs, or the IDs are genuinely missing, start selection
    if (isAdmin && (!userBusinessId || !userBranchId)) {
      setStep('select-company');
      loadAdminBusinesses();
    } else {
      setStep('categories');
    }
    setActiveCategory(null);
    setActiveTab(null);
    setSelectedCustomer(null);
    setTabData(null);
    setMessage(null);
    setInitialHiDone(false);
  };

  const loadAdminBusinesses = async () => {
    setIsLoading(true);
    try {
      const resp = await getBusinesses({ business_Active: true });
      const data = resp.data || resp || [];
      const options = data.map(b => ({ value: b.business_Id, label: b.business_NameEn || b.business_Name }));
      setAdminBusinesses(options);
    } catch (err) {
      console.error("Error loading admin businesses:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAdminBranches = async (businessId) => {
    setIsLoading(true);
    try {
      const resp = await getBranches({ Business_Id: businessId, branch_Active: true });
      const data = resp.data || resp || [];
      const options = data.map(b => ({ value: b.branch_Id, label: b.branch_NameEn || b.branch_Name }));
      setAdminBranches(options);
    } catch (err) {
      console.error("Error loading admin branches:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminBusinessSelect = (option) => {
    setSelectedAdminBusiness(option);
    setSelectedAdminBranch(null);
    setStep('select-branch');
    loadAdminBranches(option.value);
  };

  const handleAdminBranchSelect = (option) => {
    setSelectedAdminBranch(option);
    setStep('categories');
  };

  const handleCategoryClick = (catId) => {
    setActiveCategory(catId);
    if (catId === 'customer') {
      setStep('searching');
    } else {
      setStep('subOptions');
    }
    setMessage(null);
  };

  const handleOptionClick = (option) => {
    if (option.dummy) {
      setMessage(`"${option.name}" will be available in the next update. I'm focusing on Customer Management for now!`);
      return;
    }

    if (option.id === 'add-bk') {
      navigate('/GmsAddBooking');
      closeChatbot();
      return;
    }

    setActiveTab(option.id);
    setMessage(null);
    
    // If it's a generic operational report (not customer-specific), go to result
    if (['summary', 'today-bk', 'collection', 'outstanding', 'campaign-stats', 'source-stats', 'staff-duty', 'leave-req'].includes(option.id)) {
      setStep('result');
      loadTabData(option.id);
    } else if (selectedCustomer) {
      // New: If we already have a customer, go directly to results
      setStep('result');
      loadTabData(option.id, selectedCustomer);
    } else {
      setStep('searching');
    }
  };

  // Handle customer search
  const loadOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) return [];
    try {
      const response = await searchCustomers(inputValue, 1, 20);
      const items = response.items || response || [];
      return items.map(c => ({
        value: c.customer_Id,
        label: `${c.customer_NameEn} (${c.customer_ContactNo || 'N/A'})`,
        ...c
      }));
    } catch (err) {
      console.error('Error searching customers:', err);
      return [];
    }
  };

  const handleCustomerSelect = (option) => {
    setSelectedCustomer(option);
    if (option) {
       fetchCounts(option.customer_Id);
       if (activeCategory === 'customer') {
         // New flow: Customer selected, now show management options for them
         setStep('subOptions');
       } else {
         // Old flow/fallback: Option was chosen first, then customer
         setStep('result');
         loadTabData(activeTab, option);
       }
    }
  };

  const fetchCounts = async (customerId) => {
    try {
      const data = await getCustomerMemberships({ customerId });
      const memberships = data.filter(m => {
        const type = m.entity_Type || m.entityType;
        return type === 3 || type === 'sep' || type === 'SER' || type === 'PackageSeries';
      }).length;
      const packages = data.filter(m => {
        const type = m.entity_Type || m.entityType;
        return type === 2 || type === 'pac' || type === 'PAC' || type === 'Package';
      }).length;
      setCounts({ memberships, packages });
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  const loadTabData = async (tab, customer) => {
    // For non-customer-specific tabs, we don't need a customer
    const isGeneric = ['summary', 'today-bk', 'collection', 'outstanding', 'campaign-stats', 'source-stats', 'staff-duty', 'leave-req'].includes(tab);
    const cust = customer || selectedCustomer;
    if (!cust && !isGeneric) return;

    setTabLoading(true);
    setTabData(null);
    try {
      const customerId = cust?.customer_Id;
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      let data = null;

      switch (tab) {
        case 'summary':
          console.log("📊 [Assistant] Loading Daily Summary for", todayStr, "Filter:", { business_Id: currentBusinessId, branch_Id: currentBranchId });
          const summaryRes = await getInvoiceReportSummary({ 
            fromDate: todayStr, 
            toDate: todayStr,
            business_Id: currentBusinessId,
            branch_Id: currentBranchId
          });
          data = summaryRes.summaries || summaryRes.Summaries || summaryRes || [];
          break;
        case 'today-bk':
          console.log("📅 [Assistant] Loading Today's Bookings for", todayStr, "Filter:", { business_Id: currentBusinessId, branch_Id: currentBranchId });
          const tBks = await getBookings({ 
            fromDate: todayStr, 
            toDate: todayStr, 
            business_Id: currentBusinessId,
            branch_Id: currentBranchId,
            page: 1, 
            pageSize: 50 
          });
          const rawBks = tBks.bookings || tBks || [];
          const filteredBks = rawBks.filter(b => (b.booking_Date || b.from_Time || '').startsWith(todayStr));
          
          if (filteredBks.length > 0) {
            const allSvcs = await getSpaServices().catch(() => []);
            const uniqueCustIds = [...new Set(filteredBks.map(b => b.customer_Id))].filter(id => id);
            const custPromises = uniqueCustIds.map(id => getCustomers({ customer_Id: id }).catch(() => []));
            const allCustsRes = await Promise.all(custPromises);
            const allCusts = allCustsRes.flat();

            data = filteredBks.map(b => {
              const customer = allCusts.find(c => c.customer_Id === b.customer_Id);
              const firstDetail = b.details?.[0];
              const serviceId = firstDetail?.entity_Id || (firstDetail?.resourceDetails && firstDetail.resourceDetails[0]?.service_Id);
              const service = allSvcs.find(s => (s.spaService_Id || s.service_Id) === serviceId);
              
              return {
                ...b,
                customer_NameEn: customer?.customer_NameEn || b.customer_NameEn,
                details: b.details?.map((d, i) => i === 0 ? { 
                  ...d, 
                  serviceName: service?.spaServiceName_En || service?.spaService_NameEn || d.serviceName 
                } : d)
              };
            });
          } else {
            data = [];
          }
          break;
        case 'profile':
          const credit = await getCustomerOutstandingCredit(customerId).catch(() => null);
          data = { 
            name: cust.customer_NameEn,
            email: cust.customer_Email,
            phone: cust.customer_ContactNo,
            address: cust.customer_AddressEn,
            creditLimit: cust.customer_CreditLimit,
            outstanding: credit?.totalOutstanding || 0
          };
          break;
        case 'notes':
          data = cust.customer_Notes || 'No notes available.';
          break;
        case 'appointment':
          const appointmentsRes = await getBookings({ customer_Id: customerId, page: 1, pageSize: 5 });
          const appointments = appointmentsRes.bookings || appointmentsRes || [];
          if (appointments.length > 0) {
            const allSvcs = await getSpaServices().catch(() => []);
            data = appointments.map(a => {
              const firstDetail = a.details?.[0];
              const serviceId = firstDetail?.entity_Id || (firstDetail?.resourceDetails && firstDetail.resourceDetails[0]?.service_Id);
              const service = allSvcs.find(s => (s.spaService_Id || s.service_Id) === serviceId);
              
              return {
                ...a,
                details: a.details?.map((d, i) => i === 0 ? { 
                  ...d, 
                  serviceName: service?.spaServiceName_En || service?.spaService_NameEn || d.serviceName 
                } : d)
              };
            });
          } else {
            data = [];
          }
          break;
        case 'products':
          const [invoices, allProducts] = await Promise.all([
            getSalesInvoices({ Customer_Id: customerId }),
            getProducts({ status: true }).catch(() => [])
          ]);
          const products = [];
          invoices.forEach(inv => {
            if (inv.details) {
              inv.details.forEach(d => {
                const type = d.entityType?.toLowerCase();
                if (type === 'pro' || type === 'product' || type === 'prod') {
                  const productObj = allProducts.find(p => String(p.product_Id) === String(d.entityId));
                  products.push({
                     name: productObj?.product_NameEn || d.entityName || 'Product',
                     date: inv.Invoice_Date,
                     qty: d.quantity,
                     amount: d.netAmount
                  });
                }
              });
            }
          });
          data = products.slice(0, 10);
          break;
        case 'memberships':
        case 'packages':
          const [mData, allPkgs, allSeries] = await Promise.all([
            getCustomerMemberships({ customerId }).catch(() => []),
            getSpaPackages().catch(() => []),
            getSpaPackageSeries().catch(() => [])
          ]);
          
          const filtered = mData.filter(m => {
            const typeValue = m.entity_Type || m.entityType;
            if (tab === 'memberships') {
              return typeValue === 3 || String(typeValue).toLowerCase() === 'sep' || String(typeValue).toLowerCase() === 'ser' || String(typeValue).toLowerCase() === 'packageseries';
            } else {
              return typeValue === 2 || String(typeValue).toLowerCase() === 'pac' || String(typeValue).toLowerCase() === 'package';
            }
          });

          data = filtered.map(m => {
            const typeValue = String(m.entity_Type || m.entityType).toLowerCase();
            const eId = m.entity_Id || m.entityId;
            let name = `ID: ${eId}`;
            
            if (typeValue === 'pac' || typeValue === 'package' || String(typeValue) === '2') {
              const pkg = allPkgs.find(p => String(p.packageId) === String(eId));
              if (pkg) name = pkg.packageName_En || pkg.packageName;
            } else if (typeValue === 'sep' || typeValue === 'ser' || typeValue === 'packageseries' || String(typeValue) === '3') {
              const ser = allSeries.find(s => String(s.seriesId) === String(eId));
              if (ser) name = ser.seriesName_En || ser.seriesName;
            }
            return { ...m, entityName: name };
          });
          break;
        case 'unbilled':
          data = await getCustomerUnbilledServices({ CustomerId: customerId }).catch(() => []);
          break;
        case 'paymentHistory':
          const receiptsData = await getReceipts({ Type: "cu", CustomerId: customerId, Page: 1, PageSize: 10 });
          data = receiptsData.receipts || receiptsData || [];
          break;
        case 'whatsapp':
          const cleanPhone = cust.customer_ContactNo?.trim().replace(/^0+/, '').replace(/\D/g, '');
          if (cleanPhone) {
            const res = await axios.get(`${BASE_URL}/api/WhatsAppWebhook/messages-by-number/${cleanPhone}`);
            data = res.data;
          } else {
            data = [];
          }
          break;
        case 'collection':
          console.log("📊 [Assistant] Loading Daily Collection for", todayStr);
          const collFilter = { 
            fromDate: todayStr, 
            toDate: todayStr,
            business_Id: currentBusinessId,
            branch_Id: currentBranchId
          };
          console.log("📊 [Assistant] API Filter:", collFilter);
          const collRes = await getInvoiceReportSummary(collFilter);
          console.log("📊 [Assistant] API Response:", collRes);
          
          const summaries = collRes?.summaries || collRes?.Summaries || [];
          const collData = Array.isArray(summaries) && summaries.length > 0 ? summaries[0] : null;
          
          if (collData) {
            console.log("📊 [Assistant] Found collection data:", collData);
            data = [
              { mode: 'Cash', amount: collData.cash || collData.Cash || 0 },
              { mode: 'Credit Card', amount: collData.creditCard || collData.CreditCard || 0 },
              { mode: 'Bank Transfer', amount: collData.bankTransfer || collData.BankTransfer || 0 },
              { mode: 'Online', amount: collData.online || collData.Online || 0 },
              { mode: 'Tabee', amount: collData.tabee || collData.Tabee || 0 },
            ];
          } else if (Array.isArray(collRes) && collRes.length > 0) {
             const first = collRes[0];
             data = [
              { mode: 'Cash', amount: first.cash || first.Cash || 0 },
              { mode: 'Credit Card', amount: first.creditCard || first.CreditCard || 0 },
              { mode: 'Bank Transfer', amount: first.bankTransfer || first.BankTransfer || 0 },
              { mode: 'Online', amount: first.online || first.Online || 0 },
              { mode: 'Tabee', amount: first.tabee || first.Tabee || 0 },
            ];
          } else {
            console.log("📊 [Assistant] No collection data found for today.");
            data = [];
          }
          break;
        case 'outstanding':
          console.log("💳 [Assistant] Loading Outstanding List (60 Days)");
          // Fetch last 60 days of credit invoices as a proxy for outstanding balances
          const fromDate = new Date();
          fromDate.setDate(fromDate.getDate() - 60);
          const fromDateStr = fromDate.toISOString().split('T')[0];
          const toDateStr = new Date().toISOString().split('T')[0];
          
          const creditFilter = { 
            FromDate: fromDateStr, 
            ToDate: toDateStr,
            Invoice_Type: 'cr', // Credit only
            Business_Id: currentBusinessId,
            Branch_Id: currentBranchId
          };
          console.log("💳 [Assistant] API Filter:", creditFilter);
          
          // Using getSalesInvoices instead of report details because it supports ranges safely
          const creditInvoicesRes = await getSalesInvoices(creditFilter).catch(() => []);
          console.log("💳 [Assistant] API Response:", creditInvoicesRes);
          
          const creditInvoices = Array.isArray(creditInvoicesRes) ? creditInvoicesRes : (creditInvoicesRes.items || []);
          
          if (creditInvoices.length > 0) {
            // Group by CUSTOMER ID first (since names might be missing in invoice list)
            const groupedById = creditInvoices.reduce((acc, inv) => {
               const id = inv.invoice_CustomerId || inv.Invoice_CustomerId;
               if (!id) return acc;
               const creditAmt = inv.invoice_NetAmount || inv.Invoice_NetAmount || (inv.invoice_TotalAmount - inv.paidAmount) || 0;
               if (!acc[id]) acc[id] = 0;
               acc[id] += creditAmt;
               return acc;
            }, {});

            // Fetch names for unique IDs
            const uniqueIds = Object.keys(groupedById);
            const custPromises = uniqueIds.map(id => getCustomers({ customer_Id: id }).catch(() => []));
            const allCustsRes = await Promise.all(custPromises);
            const allCusts = allCustsRes.flat();
            
            data = Object.entries(groupedById)
              .map(([id, amount]) => {
                 const customer = allCusts.find(c => String(c.customer_Id) === String(id));
                 return { 
                   name: customer?.customer_NameEn || `Customer #${id}`, 
                   amount 
                 };
              })
              .sort((a, b) => b.amount - a.amount)
              .slice(0, 10);
              
            console.log("💳 [Assistant] Processed Outstanding Data With Names:", data);
          } else {
            console.log("💳 [Assistant] No credit invoices found in last 60 days.");
            data = [];
          }
          break;
        case 'campaign-stats':
          console.log("📈 [Assistant] Loading Campaign Stats");
          const campaignRes = await getCampaignReport({
            BusinessId: currentBusinessId,
            BranchId: currentBranchId,
            CampaignId: null,
            CampaignIds: null,
            SourceId: null,
            FromDate: null,
            ToDate: null
          });
          data = campaignRes?.data || campaignRes?.Data || campaignRes || [];
          break;
        case 'source-stats':
          console.log("🌐 [Assistant] Loading Source Stats (Current Month)");
          const now = new Date();
          const firstDayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
          const sourceRes = await getSourceBasedReportSummary({
            fromDate: firstDayStr,
            toDate: todayStr
          });
          data = sourceRes?.sourceSummaries || sourceRes?.SourceSummaries || [];
          break;
        case 'staff-duty':
          console.log("👥 [Assistant] Loading Staff on Duty");
          // Get all employees for the branch first
          const empList = await getEmployees({ 
             employee_Business_Id: currentBusinessId,
             employee_Branch_Id: currentBranchId,
             OnWork: true 
          });
          const attRes = await getDailyAttendance({ Date: todayStr });
          const attData = Array.isArray(attRes) ? attRes : (attRes.data || []);
          
          if (attData.length === 0) {
            data = { notSaved: true };
          } else {
            data = empList.map(e => {
              const entry = attData.find(a => String(a.employeeID || a.EmployeeID) === String(e.employee_Id));
              return {
                name: e.employee_NameEn || e.employee_Name,
                code: e.employee_Code,
                status: entry ? entry.status : 1 // Default to 1 (Present) if not set
              };
            });
          }
          break;
        case 'leave-req':
          console.log("📅 [Assistant] Loading Leave Requests");
          const [leavesRes, leaveTypesRes, empSearchRes] = await Promise.all([
             getEmployeeLeaves({}),
             getLeaveTypes({}),
             getEmployees({ employee_Business_Id: currentBusinessId, employee_Branch_Id: currentBranchId })
          ]);
          const rawLeaves = leavesRes?.data || leavesRes || [];
          const activeLeaves = rawLeaves.filter(l => !l.is_Cancelled && !l.Is_Cancelled);
          
          data = activeLeaves.map(l => {
             const emp = empSearchRes.find(e => String(e.employee_Id) === String(l.leave_Employee_Id));
             const type = leaveTypesRes.find(t => String(t.leaveType_Id) === String(l.leave_LeaveType_Id));
             return {
                empName: emp?.employee_NameEn || `ID: ${l.leave_Employee_Id}`,
                type: type?.leaveType_NameEn || 'General Leave',
                from: l.leave_FromDate ? l.leave_FromDate.split('T')[0] : '',
                to: l.leave_ToDate ? l.leave_ToDate.split('T')[0] : '',
                days: l.leave_Days,
                status: (l.leave_ApprovedDate || l.Leave_ApprovedDate) ? 'Approved' : 'Pending'
             };
          }).sort((a, b) => new Date(b.from) - new Date(a.from)).slice(0, 15);
          break;
        default:
          break;
      }
      setTabData(data);
    } catch (err) {
      console.error(`Error loading ${tab} data:`, err);
      setTabData({ error: 'Failed to load data' });
    } finally {
      setTabLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const renderTabContent = () => {
    if (tabLoading) return <div className="text-center py-4"><Spinner size="sm" color="primary" /></div>;
    if (!tabData) return null;
    if (tabData.error) return <div className="text-danger small">{tabData.error}</div>;

    switch (activeTab) {
      case 'summary':
        const summary = Array.isArray(tabData) ? tabData[0] : tabData;
        return (
          <div className="summary-data">
             {summary ? (
               <Table size="sm" borderless striped>
                 <tbody>
                    <tr><td>Total Customers</td><td className="text-end">{summary.customerCount || summary.totalInvoices || 0}</td></tr>
                    <tr><td>Gross Sales</td><td className="text-end">{formatCurrency(summary.grossSales || summary.totalGrossAmount || summary.grossAmount)}</td></tr>
                    <tr><td>Discount</td><td className="text-end">{formatCurrency(summary.discount || summary.totalDiscount)}</td></tr>
                    <tr><td>Net Sales</td><td className="text-end"><strong>{formatCurrency(summary.netSales || summary.totalNetAmount || summary.netAmount)} {currencyCode}</strong></td></tr>
                 </tbody>
               </Table>
             ) : <div className="text-center py-2 text-muted small">No sales today</div>}
          </div>
        );
      case 'today-bk':
        return (
          <div className="today-bookings" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <Table size="sm" borderless striped hover className="mb-0">
              <thead className="sticky-top bg-white"><tr><th>Time</th><th>Customer</th><th>Service</th></tr></thead>
              <tbody>
                {tabData.length > 0 ? tabData.map((b, i) => (
                  <tr key={i}>
                    <td>{new Date(b.from_Time || b.booking_Date || b.entry_Date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="small">{b.customer_NameEn || 'Walk-in'}</td>
                    <td className="small truncate-text" style={{ maxWidth: '80px' }}>{b.details?.[0]?.serviceName || 'Service'}</td>
                  </tr>
                )) : <tr><td colSpan="3" className="text-center text-muted small">No bookings today</td></tr>}
              </tbody>
            </Table>
          </div>
        );
      case 'profile':
        return (
          <div className="data-list">
            <div className="data-item"><span className="label">Email:</span> {tabData.email || '-'}</div>
            <div className="data-item"><span className="label">Phone:</span> {tabData.phone || '-'}</div>
            <div className="data-item"><span className="label">Address:</span> {tabData.address || '-'}</div>
            <div className="data-item"><span className="label">Credit Limit:</span> {formatCurrency(tabData.creditLimit)} {currencyCode}</div>
            <div className="data-item"><span className="label">Outstanding:</span> <span className="text-danger">{formatCurrency(tabData.outstanding)} {currencyCode}</span></div>
          </div>
        );
      case 'notes':
        return <div className="small p-2 bg-light rounded">{tabData}</div>;
      case 'appointment':
        return (
          <Table size="sm" borderless striped>
            <thead><tr><th>Date</th><th>Service</th><th>Status</th></tr></thead>
            <tbody>
              {tabData.length > 0 ? tabData.map((a, i) => (
                <tr key={i}>
                  <td>{formatDate(a.booking_Date || a.entry_Date)}</td>
                  <td>{a.details?.[0]?.serviceName || 'Service'}</td>
                  <td>
                    <Badge 
                      style={{ 
                        backgroundColor: a.is_Cancelled ? '#dc3545' : (a.is_CheckIn ? '#0acf97' : '#28a745'),
                        color: '#fff',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        minWidth: '65px',
                        padding: '4px 6px'
                      }}
                      className="border-0"
                    >
                      {a.is_Cancelled ? 'Cancelled' : (a.is_CheckIn ? 'Checked-In' : 'Booked')}
                    </Badge>
                  </td>
                </tr>
              )) : <tr><td colSpan="3" className="text-center text-muted">No appointments</td></tr>}
            </tbody>
          </Table>
        );
      case 'products':
        return (
          <Table size="sm" borderless striped>
            <thead><tr><th>Product</th><th>Qty</th><th>Amount</th></tr></thead>
            <tbody>
              {tabData.length > 0 ? tabData.map((p, i) => (
                <tr key={i}>
                  <td>{p.name}</td>
                  <td>{p.qty}</td>
                  <td>{formatCurrency(p.amount)}</td>
                </tr>
              )) : <tr><td colSpan="3" className="text-center text-muted">No products</td></tr>}
            </tbody>
          </Table>
        );
      case 'memberships':
      case 'packages':
        return (
          <Table size="sm" borderless striped>
            <thead><tr><th>Date</th><th>Name</th></tr></thead>
            <tbody>
              {tabData.length > 0 ? tabData.map((m, i) => (
                <tr key={i}>
                   <td>{formatDate(m.purchaseDate || m.PurchaseDate)}</td>
                   <td>{m.entityName || m.packageName || m.seriesName || `ID: ${m.entity_Id}`}</td>
                </tr>
              )) : <tr><td colSpan="2" className="text-center text-muted">None found</td></tr>}
            </tbody>
          </Table>
        );
      case 'unbilled':
        return (
          <Table size="sm" borderless striped>
            <thead><tr><th>Service</th><th>Amount</th></tr></thead>
            <tbody>
              {tabData.length > 0 ? tabData.map((s, i) => (
                <tr key={i}>
                  <td>{s.serviceName || s.ServiceName}</td>
                  <td>{formatCurrency(s.calculatedAmount || s.CalculatedAmount)}</td>
                </tr>
              )) : <tr><td colSpan="2" className="text-center text-muted">No unbilled services</td></tr>}
            </tbody>
          </Table>
        );
      case 'paymentHistory':
        return (
          <Table size="sm" borderless striped>
            <thead><tr><th>Date</th><th>Type</th><th>Amount</th></tr></thead>
            <tbody>
              {tabData.length > 0 ? tabData.map((r, i) => (
                <tr key={i}>
                  <td>{formatDate(r.receiptDate || r.receipt_Date || r.Receipt_Date || r.entry_Date)}</td>
                  <td>{r.paymentType ? (String(r.paymentType).toLowerCase() === 'ca' ? 'Cash' : String(r.paymentType).toLowerCase() === 'cc' ? 'Card' : r.paymentType.toUpperCase()) : (r.receipt_Type || 'Receipt')}</td>
                  <td>{formatCurrency(r.amount || r.receipt_Amount || r.Receipt_Amount)}</td>
                </tr>
              )) : <tr><td colSpan="3" className="text-center text-muted">No history</td></tr>}
            </tbody>
          </Table>
        );
      case 'whatsapp':
        return (
          <div className="whatsapp-msgs" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {tabData.length > 0 ? tabData.map((msg, i) => {
              const body = msg.MessageBody || msg.messageText || msg.messageBody;
              const date = msg.ReceivedAt || msg.entry_Date || msg.receivedAt;
              const incoming = msg.isIncoming !== undefined ? msg.isIncoming : true; // Default to incoming if unknown

              return (
                <div key={i} className={`mb-2 p-2 rounded ${incoming ? 'bg-light text-start' : 'bg-primary text-white text-end'}`} 
                    style={{ fontSize: '11px', maxWidth: '80%', marginLeft: incoming ? '0' : 'auto' }}>
                  <div>{body}</div>
                  <small className={incoming ? 'text-muted' : 'text-white-50'}>{formatDate(date)}</small>
                </div>
              );
            }) : <div className="text-center text-muted py-2">No messages</div>}
          </div>
        );
      case 'collection':
        return (
          <Table size="sm" borderless striped hover>
            <thead><tr><th>Mode</th><th className="text-end">Amount</th></tr></thead>
            <tbody>
              {tabData.length > 0 ? tabData.map((m, i) => (
                <tr key={i}>
                  <td className="small">{m.mode}</td>
                  <td className="text-end small">{formatCurrency(m.amount)}</td>
                </tr>
              )) : <tr><td colSpan="2" className="text-center text-muted small">No collections today</td></tr>}
            </tbody>
            {tabData.length > 0 && (
              <tfoot className="border-top">
                <tr>
                   <td className="small font-weight-bold">Total Collection</td>
                   <td className="text-end small font-weight-bold">{formatCurrency(tabData.reduce((sum, m) => sum + m.amount, 0))}</td>
                </tr>
              </tfoot>
            )}
          </Table>
        );
      case 'outstanding':
        return (
          <Table size="sm" borderless striped hover>
            <thead><tr><th>Customer</th><th className="text-end">Credit Volume (60d)</th></tr></thead>
            <tbody>
              {tabData.length > 0 ? tabData.map((o, i) => (
                <tr key={i}>
                  <td className="small">{o.name}</td>
                  <td className="text-end small"><strong>{formatCurrency(o.amount)} {currencyCode}</strong></td>
                </tr>
              )) : <tr><td colSpan="2" className="text-center text-muted small">No outstanding credits</td></tr>}
            </tbody>
          </Table>
        );
      case 'campaign-stats':
        return (
          <div className="table-responsive">
            <Table size="sm" borderless striped hover>
              <thead><tr><th>Campaign</th><th className="text-end">Spend</th><th className="text-end">Net Billed</th></tr></thead>
              <tbody>
                {tabData.length > 0 ? tabData.map((c, i) => (
                  <tr key={i}>
                    <td className="small font-weight-bold">{c.campaignName || c.CampaignName}</td>
                    <td className="text-end small text-danger">{formatCurrency(c.campaignSpend || c.CampaignSpend)}</td>
                    <td className="text-end small text-success"><strong>{formatCurrency(c.netBilled || c.NetBilled)}</strong></td>
                  </tr>
                )) : <tr><td colSpan="3" className="text-center text-muted small">No active campaign data</td></tr>}
              </tbody>
            </Table>
          </div>
        );
      case 'source-stats':
        return (
          <div className="table-responsive">
            <Table size="sm" borderless striped hover>
              <thead><tr><th>Source</th><th className="text-end">Invoiced</th><th className="text-end">Direct</th></tr></thead>
              <tbody>
                {tabData.length > 0 ? tabData.map((s, i) => (
                  <tr key={i}>
                    <td className="small font-weight-bold">{s.sourceName || s.SourceName}</td>
                    <td className="text-end small">{formatCurrency(s.invoicedRevenue || s.InvoicedRevenue)}</td>
                    <td className="text-end small text-danger"><strong>{formatCurrency(s.directRevenue || s.DirectRevenue)}</strong></td>
                  </tr>
                )) : <tr><td colSpan="3" className="text-center text-muted small">No source distribution data</td></tr>}
              </tbody>
            </Table>
          </div>
        );
      case 'staff-duty':
        if (tabData?.notSaved) {
            return (
              <div className="text-center py-4 text-muted">
                <FileText size={40} style={{ opacity: 0.2 }} />
                <p className="mt-2 mb-0 small">Attendance is not saved for today.</p>
                <p className="small" style={{ fontSize: '10px' }}>Please check the Daily Attendance page.</p>
              </div>
            );
        }
        const getStatusBadge = (s) => {
          let bg = "#28a745";
          let label = "Present";
          let text = "#fff";
          if (s === 0.5) { bg = "#ffbc00"; label = "Half Day"; text = "#000"; }
          else if (s === 0) { bg = "#39afd1"; label = "On Leave"; }
          else if (s === -1) { bg = "#fa5c7c"; label = "Absent"; }
          else if (s === -2) { bg = "#6c757d"; label = "Off Day"; }
          
          return (
            <Badge style={{ backgroundColor: bg, color: text, fontSize: '9px', fontWeight: 'bold', minWidth: '65px', padding: '4px 6px' }} className="border-0">
              {label}
            </Badge>
          );
        };
        return (
          <div className="table-responsive">
            <Table size="sm" borderless striped hover>
              <thead><tr><th>Staff</th><th className="text-center">Status</th></tr></thead>
              <tbody>
                {tabData.length > 0 ? tabData.map((e, i) => (
                  <tr key={i}>
                    <td className="small">
                      <div className="font-weight-bold">{e.name}</div>
                      <div className="text-muted" style={{ fontSize: '10px' }}>{e.code}</div>
                    </td>
                    <td className="text-center">{getStatusBadge(e.status)}</td>
                  </tr>
                )) : <tr><td colSpan="2" className="text-center text-muted small">No staff data available</td></tr>}
              </tbody>
            </Table>
          </div>
        );
      case 'leave-req':
        return (
          <div className="table-responsive">
            <Table size="sm" borderless striped hover>
              <thead><tr><th>Employee</th><th>Dates</th><th className="text-center">Status</th></tr></thead>
              <tbody>
                {tabData.length > 0 ? tabData.map((l, i) => (
                  <tr key={i}>
                    <td className="small">
                      <div className="font-weight-bold">{l.empName}</div>
                      <div className="text-muted" style={{ fontSize: '10px' }}>{l.type} ({l.days}d)</div>
                    </td>
                    <td className="small" style={{ whiteSpace: 'nowrap' }}>
                       <div>{l.from}</div>
                       <div className="text-muted">to {l.to}</div>
                    </td>
                    <td className="text-center align-middle">
                       <Badge 
                         style={{ 
                            backgroundColor: l.status === 'Approved' ? '#28a745' : '#ffbc00', 
                            color: l.status === 'Approved' ? '#fff' : '#000',
                            fontSize: '9px', 
                            fontWeight: 'bold',
                            padding: '4px 8px'
                         }} 
                         className="border-0"
                       >
                         {l.status}
                       </Badge>
                    </td>
                  </tr>
                )) : <tr><td colSpan="3" className="text-center text-muted small">No recent leave requests</td></tr>}
              </tbody>
            </Table>
          </div>
        );
      default:
        return null;
    }
  };

  const getInitials = (name) => {
    if (!name) return 'CU';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const getOptionLabel = (tabId) => {
    const allOptions = Object.values(subOptions).flat();
    return allOptions.find(o => o.id === tabId)?.name || tabId;
  };

  return (
    <div className="spa-assistant-chatbot">
      <button className="chatbot-toggle" onClick={toggleChatbot}>
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {isOpen && (
        <div className="chatbot-container">
          <div className="chatbot-header">
            <div className="d-flex align-items-center">
              <ArrowBigUpDash size={18} className="me-2" />
              <h5>Spa Assistant</h5>
            </div>
            <div className="header-actions">
               <button onClick={closeChatbot}><X size={16} /></button>
            </div>
          </div>

          <div className="chatbot-body">
            {step === 'select-company' && (
              <div className="animate__animated animate__fadeIn">
                <div className="chat-bubble bot-bubble">Hi! I am Spa Assistant.</div>
                <div className="chat-bubble bot-bubble">Please select a company to continue.</div>
                <div className="search-section mt-3">
                  <Select
                    placeholder="Select Company..."
                    options={adminBusinesses}
                    isLoading={isLoading}
                    onChange={handleAdminBusinessSelect}
                    styles={selectStyles}
                  />
                </div>
              </div>
            )}

            {step === 'select-branch' && (
              <div className="animate__animated animate__fadeIn">
                <div className="d-flex align-items-center mb-3">
                   <Button color="link" size="sm" onClick={() => setStep('select-company')} className="p-0 text-muted me-2">
                     <ChevronLeft size={18} />
                   </Button>
                   <span className="small font-weight-bold">Back to Companies</span>
                </div>
                <div className="chat-bubble bot-bubble">Great! Now please select a branch for <strong>{selectedAdminBusiness?.label}</strong>.</div>
                <div className="search-section mt-3">
                  <Select
                    placeholder="Select Branch..."
                    options={adminBranches}
                    isLoading={isLoading}
                    onChange={handleAdminBranchSelect}
                    styles={selectStyles}
                  />
                </div>
              </div>
            )}

            {step === 'categories' && (
              <div className="animate__animated animate__fadeIn">
                <div className="chat-bubble bot-bubble">
                  {(!userBusinessId || !userBranchId) ? 
                    `Exploring data for ${selectedAdminBranch?.label} (${selectedAdminBusiness?.label}). What would you like to see?` : 
                    "Hi! How can I help you today?"}
                </div>
                <div className="category-list mt-3">
                  {permissionsLoading ? (
                    <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
                  ) : (
                    accessibleCategories.map(cat => (
                      <div key={cat.id} className="category-item" onClick={() => handleCategoryClick(cat.id)}>
                        <div className="cat-icon" style={{ background: cat.color }}>{cat.icon}</div>
                        <div className="cat-info">
                          <h6>{cat.name}</h6>
                          <span className="small text-muted">{cat.id === 'customer' ? 'Fully Active' : 'Insights & Reports'}</span>
                        </div>
                        <ChevronLeft size={16} style={{ transform: 'rotate(180deg)', opacity: 0.5 }} />
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {step === 'subOptions' && (
              <div className="animate__animated animate__fadeIn">
                <div className="d-flex align-items-center mb-3">
                   <Button color="link" size="sm" onClick={() => (activeCategory === 'customer' ? setStep('searching') : setStep('categories'))} className="p-0 text-muted me-2">
                     <ChevronLeft size={18} />
                   </Button>
                   <span className="small font-weight-bold">
                     {activeCategory === 'customer' ? `Managing: ${selectedCustomer?.customer_NameEn}` : categories.find(c => c.id === activeCategory)?.name}
                   </span>
                </div>
                
                <div className="tab-options">
                  {getVisibleSubOptions(activeCategory).map(opt => (
                    <div key={opt.id} 
                         className="tab-btn" 
                         style={opt.fullWidth ? { gridColumn: 'span 2' } : {}}
                         onClick={() => handleOptionClick(opt)}>
                      {opt.icon}
                      <span>{opt.name}</span>
                      {opt.dummy && <Badge color="light" className="text-muted" style={{ fontSize: '8px' }}>Soon</Badge>}
                    </div>
                  ))}
                </div>

                {message && (
                  <div className="chat-bubble bot-bubble mt-3 animate__animated animate__bounceIn">
                    {message}
                  </div>
                )}
              </div>
            )}

            {step === 'searching' && (
              <div className="animate__animated animate__fadeIn">
                <div className="d-flex align-items-center mb-3">
                   <Button color="link" size="sm" onClick={() => setStep('categories')} className="p-0 text-muted me-2">
                     <ChevronLeft size={18} />
                   </Button>
                   <span className="small font-weight-bold">Back to Categories</span>
                </div>
                <div className="chat-bubble bot-bubble">
                  {activeCategory === 'customer' ? "Who would you like to manage? Type at least 3 characters." : `Great! Please select a customer to see their ${getOptionLabel(activeTab)}.`}
                </div>
                
                <div className="search-section mt-3">
                   <Select
                      placeholder="Type customer name..."
                      cacheOptions
                      loadOptions={loadOptions}
                      defaultOptions={[]}
                      onChange={handleCustomerSelect}
                      isLoading={isLoading}
                      isClearable
                      isSearchable
                      autoFocus
                      classNamePrefix="select-assistant"
                      components={{
                        DropdownIndicator: () => <Search size={14} className="mx-2 text-muted" />,
                        IndicatorSeparator: () => null
                      }}
                      onInputChange={(val) => {
                        if (searchTimeout) clearTimeout(searchTimeout);
                        if (val.length >= 3) {
                          const timeout = setTimeout(() => {
                            setIsLoading(true);
                            loadOptions(val).then(opts => {
                              setSearchOptions(opts);
                              setIsLoading(false);
                            });
                          }, 500);
                          setSearchTimeout(timeout);
                        } else {
                          setSearchOptions([]);
                        }
                        return val;
                      }}
                      options={searchOptions}
                      filterOption={() => true}
                      noOptionsMessage={() => "Type 3+ characters..." }
                   />
                </div>
              </div>
            )}

            {step === 'result' && (
              <div className="animate__animated animate__fadeIn">
                <div className="d-flex align-items-center justify-content-between mb-2">
                   <Button color="link" size="sm" onClick={() => (activeCategory === 'customer' || ['summary', 'today-bk', 'collection', 'outstanding', 'campaign-stats', 'source-stats', 'staff-duty', 'leave-req'].includes(activeTab) ? setStep('subOptions') : setStep('searching'))} className="p-0 text-muted">
                     <ChevronLeft size={18} /> Back
                   </Button>
                   <Button color="link" size="sm" onClick={resetFlow} className="p-0 text-primary small">
                     New inquiry
                   </Button>
                </div>

                {selectedCustomer && activeCategory === 'customer' && (
                  <div className="customer-info-minimal mb-3">
                    <div className="avatar text-white" style={{ background: '#727cf5' }}>{getInitials(selectedCustomer?.customer_NameEn || selectedCustomer?.label)}</div>
                    <div className="details">
                      <h6>{selectedCustomer?.customer_NameEn || selectedCustomer?.label}</h6>
                      <p><Phone size={10} /> {selectedCustomer?.customer_ContactNo || 'N/A'}</p>
                      {/* Metrics removed based on user request */}
                    </div>
                  </div>
                )}

                <div className="tab-data-content border-top pt-3">
                   <div className="d-flex align-items-center mb-2">
                      <h6 className="m-0 text-primary small font-weight-bold">{getOptionLabel(activeTab)} Details</h6>
                   </div>
                   <div className="data-display">
                      {renderTabContent()}
                   </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const selectStyles = {
  control: (base) => ({
    ...base,
    fontSize: '13px',
    borderRadius: '10px',
    borderColor: '#e9ecef',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#727cf5'
    }
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    fontSize: '12px',
    backgroundColor: isSelected ? '#727cf5' : isFocused ? '#f0f2ff' : 'white',
    color: isSelected ? 'white' : '#495057',
    cursor: 'pointer'
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '10px',
    overflow: 'hidden',
    zIndex: 99
  })
};

export default SpaAssistant;
